import styled from 'styled-components';
import { FlexCol, FlexJustifySpaceBetween, FlexWrap } from '../../primitives/flex';
import { Text } from '../../primitives/typography';
import { dollarsWithCentsWithSymbol } from '../../../helpers/currency';
import useConversationDetail from '../hooks/useConversationDetail';

const BookingPricing = () => {
  const { data } = useConversationDetail();

  const {
    booking: {
      state,
      special_offer_price,
      display_captain_price_as_tbd,
      boat_price,
      pay_captain_at_dock,
      owner_listing_fee,
      owner_insurance_fee,
      captain_fee,
      user_type,
      renter_pricing,
      owner_pricing,
      renter_add_on_total,
      owner_add_on_total,
      captain_payout,
    },
  } = data;

  const displayCaptainTBD = display_captain_price_as_tbd;
  const hasCaptainPrice = captain_fee > 0;
  const displayCaptainTBDorPrice = displayCaptainTBD ? 'TBD' : dollarsWithCentsWithSymbol(captain_fee);
  const captainFeeValue = hasCaptainPrice ? dollarsWithCentsWithSymbol(captain_fee) : displayCaptainTBDorPrice;

  const renterPriceItems = [
    {
      label: 'Boat price',
      value: boat_price,
    },
    {
      label: pay_captain_at_dock ? 'Captain price (pay at dock)' : 'Captain price',
      value: captainFeeValue,
    },
    {
      label: 'Price adjustment',
      value: special_offer_price,
    },
    {
      label: 'Add-ons',
      value: renter_add_on_total,
    },
  ].filter((price) => !!price.value && price.value !== '$0.00');

  const ownerPriceItems = [
    {
      label: 'Boat price',
      value: boat_price,
    },
    ...(display_captain_price_as_tbd || (owner_pricing?.captain_price || 0) > 0
      ? [
          {
            label: 'Captain price',
            value: display_captain_price_as_tbd ? 'TBD' : dollarsWithCentsWithSymbol(owner_pricing?.captain_price),
          },
        ]
      : []),
    {
      label: 'Price adjustment',
      value: special_offer_price,
    },
    {
      label: 'Add-ons',
      value: owner_add_on_total,
    },
    {
      label: 'Listing fee',
      value: owner_listing_fee * -1,
    },
    {
      label: `Insurance${state === 'initialized' || state === 'pre_approved' ? ' (est)' : ''}`,
      value: owner_insurance_fee * -1,
    },
  ].filter((price) => !!price.value);

  const captainPriceItems = [
    {
      label: 'Captain price',
      value: captainFeeValue,
    },
  ].filter((price) => !!price.value);

  const getPricing = () => {
    if (user_type === 'renter' && (state === 'pre_approved' || state === 'initialized' || state === 'payment_held'))
      return renterPriceItems;
    if (user_type === 'owner') {
      if (state === 'concluded') return [];
      if (owner_pricing.actual_payout === 0) return ownerPriceItems;
    }

    if (user_type === 'captain') return captainPriceItems;

    return [];
  };
  const pricingItems = getPricing().filter((p) => p.value !== '$0');

  const inquiryState = state === 'initialized' || state === 'pre_approved' || state === 'payment_held';
  return (
    <FlexCol>
      <FlexCol gap="10px">
        {pricingItems.map((item, index) => (
          <FlexJustifySpaceBetween key={index}>
            <Text>{item.label}</Text>
            <ValueWrapper>
              <Text
                fWeight={600}
                fSize="15px"
                color={
                  item.label === 'Coupon discount' || item.label === 'Coupon discount' || item.label === 'Boat pass'
                    ? '#2a8500'
                    : 'inherit'
                }
              >
                {item.label.includes('Captain price') ? item.value : dollarsWithCentsWithSymbol(item.value as number)}
              </Text>
            </ValueWrapper>
          </FlexJustifySpaceBetween>
        ))}
      </FlexCol>
      {user_type === 'renter' && (
        <>
          <FlexJustifySpaceBetween pt={pricingItems.length > 0 ? '25px' : '0px'}>
            <Text fWeight={700}>{inquiryState ? 'Booking total' : 'Total'}</Text>
            <Text fWeight={700}>
              {inquiryState
                ? dollarsWithCentsWithSymbol(renter_pricing.inquiry_total_price)
                : dollarsWithCentsWithSymbol(renter_pricing.total_after_tax_and_credits)}
            </Text>
          </FlexJustifySpaceBetween>
          {inquiryState && (
            <Text color="#5e696a" fSize="12px" pt="10px">
              Additional fees will show at checkout
            </Text>
          )}
        </>
      )}

      {user_type === 'owner' && (
        <FlexJustifySpaceBetween pt="25px">
          <Text fWeight={700}>You earn</Text>
          <Text fWeight={700} color="#2a8500">
            {dollarsWithCentsWithSymbol(owner_pricing.earnings_total)}
          </Text>
        </FlexJustifySpaceBetween>
      )}

      {user_type === 'captain' && (
        <FlexJustifySpaceBetween pt="25px">
          <Text fWeight={700}>You earn</Text>
          <Text fWeight={700} color="#2a8500">
            {dollarsWithCentsWithSymbol(captain_payout)}
          </Text>
        </FlexJustifySpaceBetween>
      )}
    </FlexCol>
  );
};

const ValueWrapper = styled(FlexWrap)`
  justify-items: end;
  justify-content: end;
`;

export default BookingPricing;
